import { useCallback, useEffect, useRef, useState } from 'react';

import client from '../../shared/api/client';
import { toastError } from '../../shared/toastHelper';

// Pass the browse filters through so the facet rail stays live in suggestions
// mode (server post-filters the small suggestion set). `offset` drives paging.
const buildParams = (filters, venueOwnershipId, offset) => {
  const params = {};
  if (venueOwnershipId) params.venue_ownership_id = [venueOwnershipId];
  if (offset) params.offset = offset;
  if (filters.q) params.q = filters.q;
  if (filters.status && filters.status.length) params.status = filters.status;
  if (filters.genre && filters.genre.length) params.genre = filters.genre;
  if (filters.agency && filters.agency.length) params.agency = filters.agency;
  if (filters.avgCap) { [params.avg_cap_min, params.avg_cap_max] = filters.avgCap; }
  if (filters.spotify) { [params.spotify_min, params.spotify_max] = filters.spotify; }
  return params;
};

// Append the next page's rows to the matching group (by key) and carry its
// has_more forward. Robust for the future multi-group shape.
const mergeGroups = (current, next) => ({
  ...current,
  groups: current.groups.map((group) => {
    const incoming = next.groups.find((g) => g.key === group.key);
    if (!incoming) return group;
    return { ...group, rows: [...group.rows, ...incoming.rows], has_more: incoming.has_more };
  }),
});

const loadedCount = (data) => (data ? data.groups.reduce((n, g) => n + g.rows.length, 0) : 0);

export default function useSmartSuggestions(suggestionsUrl, filters) {
  const [state, setState] = useState({ data: null, loading: true, loadingMore: false, error: false });
  const [venueOwnershipId, setVenueOwnershipId] = useState(null);
  const genRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const fetchKey = JSON.stringify(filters);

  useEffect(() => {
    genRef.current += 1;
    const gen = genRef.current;
    loadingMoreRef.current = false;
    setState((s) => ({ ...s, loading: true, error: false }));
    client
      .get(suggestionsUrl, { params: buildParams(filters, venueOwnershipId, 0) })
      .then((res) => {
        if (gen !== genRef.current) return;
        setState({ data: res.data, loading: false, loadingMore: false, error: false });
      })
      .catch(() => {
        if (gen !== genRef.current) return;
        setState((s) => ({ ...s, loading: false, error: true }));
        toastError('Could not load suggestions. Please try again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionsUrl, fetchKey, venueOwnershipId]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !state.data) return;
    const offset = loadedCount(state.data);
    if (offset >= (state.data.total || 0)) return;

    loadingMoreRef.current = true;
    const gen = genRef.current;
    setState((s) => ({ ...s, loadingMore: true }));
    client
      .get(suggestionsUrl, { params: buildParams(filters, venueOwnershipId, offset) })
      .then((res) => {
        if (gen !== genRef.current) return;
        setState((s) => ({ ...s, loadingMore: false, data: mergeGroups(s.data, res.data) }));
      })
      .catch(() => {
        if (gen !== genRef.current) return;
        setState((s) => ({ ...s, loadingMore: false }));
        toastError('Could not load more suggestions.');
      })
      .finally(() => { loadingMoreRef.current = false; });
  }, [state.data, suggestionsUrl, fetchKey, venueOwnershipId]);

  const hasMore = state.data ? state.data.groups.some((g) => g.has_more) : false;

  return { ...state, hasMore, loadMore, venueOwnershipId, setVenueOwnershipId };
}
